import React, { FC, useState } from 'react'
import { Box, Grid, CleanButton } from '@gassan-ui'
import { Heading } from '@rolex-ui'
import { MultipleItemResponse } from '@lib/kontent'
import { RolexStore } from '@lib/kontent/models/RolexStore'
import { rem } from 'polished'
import { useLocalization } from '@components/Localization'
import styled from 'styled-components'
import Retailer from './Retailer'
import { AnimatePresence, motion } from 'framer-motion'

const RetailerGrid = styled(Grid)<{ isActive: boolean }>`
  display: ${p => (p.isActive ? 'grid' : 'none')};
`

const ToggleButton = styled(CleanButton)<{ isActive: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;

  svg {
    transition: transform 0.2s ease-in-out;
    transform: ${p => !p.isActive && 'rotate(180deg)'};
  }
`

type Props = MultipleItemResponse<RolexStore>

type CountryList = {
  [key: string]: RolexStore[]
}

const splitByCountry = (items: MultipleItemResponse<RolexStore>): CountryList => {
  let countries = {}
  for (const retailer of items.items) {
    const countryName = retailer.elements.country.value[0].codename
    if (countries[countryName]) {
      countries[countryName] = [...countries[countryName], retailer.elements]
    } else {
      countries[countryName] = [retailer.elements]
    }
  }
  return countries
}

const RetailerList: FC<Props> = props => {
  const { messages } = useLocalization()
  const [activeCountry, setActiveCountry] = useState<null | number>(0)
  const countryList = splitByCountry(props)
  return (
    <Box>
      {Object.keys(countryList).map((country, index) => (
        <Box key={index}>
          <ToggleButton
            onClick={() => setActiveCountry(activeCountry !== index ? index : null)}
            isActive={activeCountry === index}
          >
            <Heading variant="pushTitle" mb={rem(20)}>
              {messages.rolex.contact[country]}
            </Heading>
            <svg
              width="16"
              height="10"
              viewBox="0 0 16 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 9L8 2L15 9" stroke="currentColor" strokeWidth="2" />
            </svg>
          </ToggleButton>
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={activeCountry}
              initial={{ y: -5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -5, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <RetailerGrid
                isActive={activeCountry === index}
                gridTemplateColumns={{ _: '1fr', large: '1fr 1fr 1fr' }}
                gridGap={rem(20)}
                mb="2.5rem"
              >
                {countryList[country].map((retailer, key) => (
                  <Retailer key={key} retailer={retailer} />
                ))}
              </RetailerGrid>
            </motion.div>
          </AnimatePresence>
        </Box>
      ))}
    </Box>
  )
}

export default RetailerList
